// Weekly roundup: three sections per ISO week (Monday–Sunday, UTC), assembled
// deterministically from published stories. Nothing is generated: headlines
// and blurbs come from titles and the first sentence of the prepared summary
// (or official excerpt); takeaways are chosen by status and phrased with a
// fixed verb ("Plan for", "Adopt", "Try"). Every card links to its source.
import type { ContentType, Story, Topic } from "./model.ts";
import { effectiveFlag, type FlagStatus } from "./flags.ts";
import { PRODUCT_BY_ID, type AreaId } from "./products.ts";
import { collapseRepeats, firstSentence } from "./digest.ts";
import { plainSummary } from "./summaryText.ts";

export const ROUNDUP_WEEKS = 26;
const WEEK = 7 * 86_400_000;

export interface RoundupCard {
  storyId: string;
  title: string;
  blurb: string;
  url: string;
  sourceName: string;
  date: number;
  topic: Topic;
  type: ContentType;
  flag: FlagStatus | null;
  isSample: boolean;
  unreviewed: boolean;
}

export interface HighlightCard {
  productId: string;
  productName: string;
  area: AreaId;
  count: number;
  blurb: string;
  items: { storyId: string; title: string; url: string; flag: FlagStatus | null }[];
}

export type TakeawayVerb = "Plan for" | "Adopt" | "Try";

export interface Takeaway {
  verb: TakeawayVerb;
  card: RoundupCard;
  productName: string | null;
  area: AreaId | null;
}

export interface RoundupWeek {
  start: number; // Monday 00:00 UTC
  end: number; // exclusive
  label: string;
  total: number;
  blogs: RoundupCard[];
  highlights: HighlightCard[];
  takeaways: Takeaway[];
}

export interface RoundupDoc {
  generatedAt: number;
  weeks: RoundupWeek[]; // newest first
}

export function isoWeekStart(t: number): number {
  const d = new Date(t);
  const day = (d.getUTCDay() + 6) % 7;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day);
}

const fmt = (t: number, year = false) =>
  new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" });

const FLAG_WEIGHT: Record<FlagStatus, number> = { ga: 3, retired: 2.5, deprecated: 2.5, public_preview: 2, private_preview: 1.5, beta: 2 };

function toCard(s: Story): RoundupCard {
  const text = plainSummary(s.summaries.plainEnglish) || s.title;
  return {
    storyId: s.id,
    title: s.title,
    blurb: firstSentence(text, 170),
    url: s.canonicalUrl,
    sourceName: s.sourceName,
    date: s.publishedAt ?? s.sortAt,
    topic: s.primaryTopic,
    type: s.contentType,
    flag: effectiveFlag(s)?.status ?? null,
    isSample: s.isSample,
    unreviewed: s.summaryProvenance === "source_excerpt",
  };
}

/**
 * Blog ranking only (never a status label): launch posts (a verified status,
 * or launch wording in the title) first, then press releases, then everything else; newest first within each.
 */
export const LAUNCH_TITLE = /^(announcing|introducing|unveiling|meet)\b|\b(generally available|now available|now in (public |private )?preview|public preview|private preview|is now ga|launch(es|ed)?)\b/i;
export function blogTier(c: Pick<RoundupCard, "title" | "type"> & { flag?: FlagStatus | null }): number {
  return c.flag || LAUNCH_TITLE.test(c.title) ? 0 : c.type === "announcement" ? 1 : 2;
}

const rank = (a: RoundupCard, b: RoundupCard) =>
  (b.flag ? FLAG_WEIGHT[b.flag] : 0) - (a.flag ? FLAG_WEIGHT[a.flag] : 0) || b.date - a.date || a.storyId.localeCompare(b.storyId);

export function buildWeek(published: Story[], start: number): RoundupWeek {
  const end = start + WEEK;
  const stories = collapseRepeats(published.filter((s) => {
    const t = s.publishedAt ?? s.sortAt;
    return t >= start && t < end;
  })).map((e) => e.story);

  const blogs = stories
    .filter((s) => s.contentType !== "release_note")
    .map(toCard)
    .sort((a, b) => blogTier(a) - blogTier(b) || b.date - a.date || a.storyId.localeCompare(b.storyId))
    .slice(0, 3);

  const byProduct = new Map<string, Story[]>();
  for (const s of stories.filter((x) => x.contentType === "release_note")) {
    const pid = s.products.find((p) => PRODUCT_BY_ID.has(p));
    if (!pid) continue;
    byProduct.set(pid, [...(byProduct.get(pid) ?? []), s]);
  }
  const highlights: HighlightCard[] = [...byProduct.entries()]
    .map(([pid, list]) => {
      const cards = list.map(toCard).sort(rank);
      const weight = cards.length + cards.reduce((n, c) => n + (c.flag ? FLAG_WEIGHT[c.flag] * 0.5 : 0), 0);
      const p = PRODUCT_BY_ID.get(pid)!;
      return {
        weight,
        card: {
          productId: pid,
          productName: p.name,
          area: p.area,
          count: cards.length,
          blurb: cards[0].blurb,
          items: cards.slice(0, 3).map((c) => ({ storyId: c.storyId, title: c.title, url: c.url, flag: c.flag })),
        } satisfies HighlightCard,
      };
    })
    .sort((a, b) => b.weight - a.weight || a.card.productId.localeCompare(b.card.productId))
    .slice(0, 4)
    .map((x) => x.card);

  // Takeaways: what to act on. Deprecations first (action required), then GA, then previews.
  const verbFor = (f: FlagStatus | null): TakeawayVerb | null =>
    f === "deprecated" || f === "retired" ? "Plan for" : f === "ga" ? "Adopt" : f === "public_preview" || f === "private_preview" || f === "beta" ? "Try" : null;
  const order: Record<TakeawayVerb, number> = { "Plan for": 0, Adopt: 1, Try: 2 };
  const usedProducts = new Set<string>();
  const takeaways: Takeaway[] = [];
  const candidates = stories
    .map((s) => ({ s, card: toCard(s), verb: verbFor(effectiveFlag(s)?.status ?? null) }))
    .filter((x): x is { s: Story; card: RoundupCard; verb: TakeawayVerb } => x.verb !== null)
    .sort((a, b) => order[a.verb] - order[b.verb] || b.card.date - a.card.date || a.card.storyId.localeCompare(b.card.storyId));
  const seenVerbs = new Set<TakeawayVerb>();
  // First pass: one per verb (variety); second pass: fill up to 3. One per product.
  for (const pass of [0, 1]) {
    for (const c of candidates) {
      if (takeaways.length >= 3) break;
      const pid = c.s.products.find((p) => PRODUCT_BY_ID.has(p)) ?? null;
      if (takeaways.some((t) => t.card.storyId === c.card.storyId)) continue;
      if (pid && usedProducts.has(pid)) continue;
      if (pass === 0 && seenVerbs.has(c.verb)) continue;
      seenVerbs.add(c.verb);
      if (pid) usedProducts.add(pid);
      takeaways.push({ verb: c.verb, card: c.card, productName: pid ? PRODUCT_BY_ID.get(pid)!.name : null, area: pid ? PRODUCT_BY_ID.get(pid)!.area : null });
    }
  }
  takeaways.sort((a, b) => order[a.verb] - order[b.verb]);

  return {
    start,
    end,
    label: `${fmt(start)} – ${fmt(end - 86_400_000, true)}`,
    total: stories.length,
    blogs,
    highlights,
    takeaways,
  };
}

export function buildRoundups(published: Story[], now = Date.now(), weeks = ROUNDUP_WEEKS): RoundupDoc {
  const current = isoWeekStart(now);
  const out: RoundupWeek[] = [];
  for (let i = 0; i < weeks; i++) out.push(buildWeek(published, current - i * WEEK));
  return { generatedAt: now, weeks: out.filter((w) => w.total > 0) };
}

/**
 * Default week: the newest week that has ended (the current week is partial),
 * or the newest week if none has.
 */
export function defaultWeek(doc: RoundupDoc, now = Date.now()): RoundupWeek | null {
  return doc.weeks.find((w) => w.end <= now) ?? doc.weeks[0] ?? null;
}

/**
 * Post text for social media, without the link (the caller appends it).
 * Fits `max` characters including the link, counted as `urlLength` (X counts
 * every link as 23). Fictional samples are never named in a post.
 */
export function roundupShareText(w: RoundupWeek, { max = 280, urlLength = 23 } = {}): string {
  const head = `Databricks Weekly Roundup · ${w.label}\n${w.total} ${w.total === 1 ? "update" : "updates"} from official Databricks sources`;
  const foot = "Assembled by Databricks Pulse. Not an official Databricks statement.";
  const titles = [
    ...w.takeaways.filter((t) => !t.card.isSample).map((t) => `${t.verb}: ${t.card.title}`),
    ...w.blogs.filter((c) => !c.isSample).map((c) => c.title),
  ].filter((t, i, all) => all.indexOf(t) === i);
  const budget = max - urlLength - 1; // newline before the link
  const compose = (lines: string[]) => [head + (lines.length ? ", including:" : "."), ...lines.map((l) => `• ${l}`), foot].join("\n");
  // Whole titles first (skipping ones that don't fit); truncate only if none fit.
  const lines: string[] = [];
  for (const t of titles) if (lines.length < 4 && compose([...lines, t]).length <= budget) lines.push(t);
  if (!lines.length && titles.length) {
    const room = budget - compose([""]).length - 1;
    if (room >= 24) lines.push(`${titles[0].slice(0, room).trimEnd()}…`);
  }
  const text = compose(lines);
  return text.length <= budget ? text : [head + ".", foot].join("\n").slice(0, budget);
}
